import * as Tone from "tone";
import { Register } from "../../utils/registerVoicing";

export interface SequencerProps {
  bpm?: number; // Default tempo in BPM
  stepsCount?: number; // Number of steps in the sequencer
  sampler?: Tone.Sampler | null; // Sampler instance for chord mode
  samplesMap?: { [note: string]: string } | null; // Samples mapping for building per-step voices
  isKeyboardMode?: boolean; // Whether keyboard mode is active
  disabled?: boolean; // Whether the sequencer should be disabled
  // Outbound events from sequencer to parent
  onIsPlayingChange?: (isPlaying: boolean) => void; // Callback when playing state changes
  onActiveNotesChange?: (notes: string[]) => void; // Callback with active notes for keyboard highlighting
  onProgressionStateChange?: (state: SequencerProgressionState) => void; // Callback when progression state changes
  // FX mapping props
  mappingMode?: string | null; // Current FX being mapped (null = normal view, "filter" = mapping filter, etc.)
  fxModes?: {
    filter: boolean; // true = per-step mode, false = global mode
    reverb: boolean;
    delay: boolean;
    reverse: boolean;
  };
  onMappingModeChange?: (mode: string | null) => void; // Callback when mapping mode changes
}

export interface SequencerSnapshot {
  selectedKey: string;
  selectedProgression: string;
  progressionState: SequencerProgressionState;
  patternPreset: string;
  pattern: Step[];
  stepsCount: number;
  ribbonPositions: Array<{ startPercent: number; endPercent: number }>;
  register: Register;
  sequencerMode: "chord" | "drone-mono" | "drone-duo";
  droneNotes: string[];
}

export interface SequencerHandle {
  prepare: () => Promise<boolean>;
  start: () => Promise<void>;
  stop: () => void;
  setKey: (key: string) => void;
  setProgression: (progression: SequencerProgressionValue) => void;
  applyPatternPreset: (presetKey: string) => void;
  setStepsCount: (count: number) => void;
  setPattern: (pattern: Step[]) => void;
  setRibbonPositions: (
    positions: Array<{ startPercent: number; endPercent: number }>
  ) => void;
  setRegister: (register: Register) => void;
  setSequencerMode: (mode: "chord" | "drone-mono" | "drone-duo") => void;
  setDroneNotes: (notes: string[]) => void;
  initializeStepsWithGlobalValue: (fxType: string, globalValue: number) => void;
  hasMappingApplied: (fxType: string) => boolean;
  getSnapshot: () => SequencerSnapshot;
}

// Define the common step interface
export interface BaseStep {
  active: boolean;
  fxMixes?: {
    filter?: number; // Filter cutoff in Hz (for filter) or mix 0-1 (for others)
    reverb?: number; // Mix 0-1
    delay?: number; // Mix 0-1
    reverse?: number; // Mix 0-1
  };
}

// Chord-specific step properties
export interface ChordStep extends BaseStep {
  mode: "chord";
}

// Drone-specific step properties
export interface DroneStep extends BaseStep {
  mode: "drone";
}

// Step type (chord or drone mode)
export type Step = ChordStep | DroneStep;

export interface ChordProgression {
  roman: string[];
  mode: "minor" | "major";
}

export interface SequencerProgressionState {
  name: string;
  roman: string[];
  mode: "minor" | "major";
}

export type SequencerProgressionValue =
  | string
  | {
      name?: string;
      roman: string[];
      mode: "minor" | "major";
    };

export interface ChordMapping {
  [stepIndex: number]: string;
}

// Drone mode types
export type DroneMode = "mono" | "duo";

export interface DroneMapping {
  notes: string[]; // Global notes that play on all active steps
}

// Pattern generation configuration
export interface PatternGeneration {
  steps: number; // 1-16: number of steps in the pattern
  bars: number; // 0.25, 0.5, 1, 2, 4, 8: how many bars the pattern spans
  rate: number; // 0.5, 0.75, 1, 1.5, 2: speed multiplier for the pattern
}

// Valid bar options for pattern
export const PATTERN_BAR_OPTIONS = [0.25, 0.5, 1, 2, 4, 8] as const;
export type PatternBarOption = (typeof PATTERN_BAR_OPTIONS)[number];

// Valid rate options for pattern
export const PATTERN_RATE_OPTIONS = [0.5, 0.75, 1, 1.5, 2] as const;
export type PatternRateOption = (typeof PATTERN_RATE_OPTIONS)[number];

// Chord timing configuration
export interface ChordTiming {
  bars: number; // 1, 2, 4, 8: how many bars the chord sequence spans
  ribbonPositions: Array<{ startPercent: number; endPercent: number }>; // Percentage-based positions (0-1)
}

// Valid bar options for chords
export const CHORD_BAR_OPTIONS = [1, 2, 4, 8] as const;
export type ChordBarOption = (typeof CHORD_BAR_OPTIONS)[number];

// Default values
export const DEFAULT_PATTERN_GENERATION: PatternGeneration = {
  steps: 16,
  bars: 1,
  rate: 1,
};

export const DEFAULT_CHORD_TIMING: ChordTiming = {
  bars: 1,
  ribbonPositions: [
    { startPercent: 0, endPercent: 0.25 },
    { startPercent: 0.25, endPercent: 0.5 },
    { startPercent: 0.5, endPercent: 0.75 },
    { startPercent: 0.75, endPercent: 1 },
  ],
};

export type ChordTrackLength = 4 | 8 | 16 | 32;
